/**
 * Lab Modal Trigger Custom Element
 * Button component that opens/closes associated lab-modal elements
 * Provides keyboard accessibility with Enter and Space key support
 *
 * @example
 * <lab-modal-trigger data-modal-id="cart">
 *   Open Cart
 * </lab-modal-trigger>
 */
class LabModalTrigger extends HTMLElement {
  constructor() {
    super();

    // Set accessibility attributes
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'button');
    }

    // Get modal ID from data attribute or fallback to element ID
    this.modalId = this.dataset.modalId || this.id;

    // Bound methods for proper cleanup
    this.boundHandleKeyDown = null;
    this.boundHandleClick = null;
    this.modalObserver = null;
  }

  connectedCallback() {
    // Validate modal ID exists
    if (!this.modalId) {
      // eslint-disable-next-line no-console
      console.error('LabModalTrigger: Must have id or data-modal-id attribute');
      return;
    }

    // Find associated modal (could be in DOM or in template)
    this.modal = document.querySelector(`lab-modal#${this.modalId}`);

    // If modal not found, check for lazy-load template
    if (!this.modal) {
      const template = document.querySelector(`template[data-modal-id="${this.modalId}"]`);
      if (template) {
        // Clone and append template content on first use
        this.lazyLoadModal(template);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`LabModalTrigger: Modal "#${this.modalId}" not found`);
        return;
      }
    }

    // Set ARIA attributes
    this.setAttribute('aria-controls', this.modalId);
    this.setAttribute('aria-haspopup', 'dialog');
    this.updateAriaExpanded();

    this.initTrigger();
    this.modalObserver = new MutationObserver(() => this.updateAriaExpanded());
    this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * Lazy load modal from template on first interaction
   * @param {HTMLTemplateElement} template - Template containing modal HTML
   */
  lazyLoadModal(template) {
    const content = template.content.cloneNode(true);
    document.body.appendChild(content);

    // Find the newly added modal
    this.modal = document.querySelector(`lab-modal#${this.modalId}`);

    // Remove template after use
    template.remove();
  }

  /**
   * Initialize event listeners with bound methods
   */
  initTrigger() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleClick = this.handleClick.bind(this);

    this.addEventListener('keydown', this.boundHandleKeyDown);
    this.addEventListener('click', this.boundHandleClick);
  }

  disconnectedCallback() {
    if (this.boundHandleKeyDown) {
      this.removeEventListener('keydown', this.boundHandleKeyDown);
      this.boundHandleKeyDown = null;
    }

    if (this.boundHandleClick) {
      this.removeEventListener('click', this.boundHandleClick);
      this.boundHandleClick = null;
    }

    if (this.modalObserver) {
      this.modalObserver.disconnect();
      this.modalObserver = null;
    }
  }

  /**
   * Update aria-expanded based on modal state
   */
  updateAriaExpanded() {
    if (!this.modal) return;

    const isModalOpen = this.modal.hasAttribute('open');
    this.setAttribute('aria-expanded', isModalOpen ? 'true' : 'false');
  }

  /**
   * Toggle modal visibility and update ARIA state
   */
  toggleModalVisibility() {
    if (!this.modal) return;

    const isModalOpen = this.modal.hasAttribute('open');
    this.modal.toggleAttribute('open', !isModalOpen);

    // Update ARIA state
    this.updateAriaExpanded();
  }

  /**
   * Handle keyboard interaction (Enter and Space keys)
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeyDown(event) {
    if (!event) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.click();
    }
  }

  /**
   * Handle click to toggle modal
   */
  handleClick() {
    this.toggleModalVisibility();
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('lab-modal-trigger')) {
  customElements.define('lab-modal-trigger', LabModalTrigger);
}
